import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { SHRINE_BOONS_BY_ID, type ShrineEvent } from '../sim/events/EventDefs';
import { CURSES_BY_ID } from '../sim/artifacts/CurseDefs';

const PANEL_W = 480;

/**
 * Cursed shrine decision: one pre-rolled boon+curse pair, accept or walk
 * away. Pauses the sim. Declining leaves the shrine available.
 */
export class ShrineOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0; // 0 = accept, 1 = walk away
  private rowTexts: Text[] = [];
  private rowY: number[] = [];
  private focusRing = new Graphics();
  private acceptDelay = 8;
  private panelX: number;

  constructor(
    private readonly ctx: GameContext,
    ev: ShrineEvent,
    private readonly onAccept: () => void,
  ) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    const panelH = 250;
    this.panelX = (w - PANEL_W) / 2;
    const panelY = (h - panelH) / 2;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.75 }));
    this.root.addChild(new Graphics()
      .roundRect(this.panelX, panelY, PANEL_W, panelH, 10)
      .fill(Palette.hudPanel)
      .stroke({ width: 2, color: 0xb56bff, alpha: 0.7 }));

    const title = new Text({
      text: 'CURSED SHRINE',
      style: {
        fontFamily: 'monospace', fontSize: 22, fontWeight: 'bold',
        fill: 0xb56bff, letterSpacing: 3,
      },
    });
    title.anchor.set(0.5, 0);
    title.position.set(w / 2, panelY + 18);
    this.root.addChild(title);

    const boon = SHRINE_BOONS_BY_ID.get(ev.offer.boonId);
    const curse = CURSES_BY_ID.get(ev.offer.curseId);
    const boonText = new Text({
      text: `boon   ${boon?.name ?? '?'} — ${boon?.desc ?? ''}`,
      style: { fontFamily: 'monospace', fontSize: 14, fill: Palette.gold },
    });
    boonText.position.set(this.panelX + 24, panelY + 62);
    this.root.addChild(boonText);

    const curseText = new Text({
      text: `curse  ${curse?.name ?? '?'} — ${curse?.desc ?? ''}   (until cleansed)`,
      style: { fontFamily: 'monospace', fontSize: 14, fill: Palette.enemyHot },
    });
    curseText.position.set(this.panelX + 24, panelY + 88);
    this.root.addChild(curseText);

    this.root.addChild(this.focusRing);
    ['Accept the bargain', 'Walk away'].forEach((label, i) => {
      const y = panelY + 136 + i * 36;
      const row = new Text({
        text: label,
        style: { fontFamily: 'monospace', fontSize: 15, fill: Palette.hudText },
      });
      row.position.set(this.panelX + 24, y);
      this.rowTexts.push(row);
      this.rowY.push(y);
      this.root.addChild(row);
    });

    const hint = new Text({
      text: 'choose  [E / Ⓐ]      leave  [Esc / Ⓑ]',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5);
    hint.position.set(w / 2, panelY + panelH - 20);
    this.root.addChild(hint);
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navY !== 0) this.focus = (this.focus + input.navY + 2) % 2;
    if (input.confirmPressed) {
      if (this.focus === 0) this.onAccept();
      this.ctx.scenes.pop();
      return;
    }
    if (input.cancelPressed || input.pausePressed) this.ctx.scenes.pop();
  }

  render(_alpha: number): void {
    const g = this.focusRing;
    g.clear();
    const y = this.rowY[this.focus]!;
    g.roundRect(this.panelX + 16, y - 6, PANEL_W - 32, 30, 6)
      .stroke({ width: 2, color: Palette.focus });
  }
}
